import Link from 'next/link';

import React, { ReactElement, useState, useRef } from 'react';
import Slider from 'react-slick';

import dayjs from 'dayjs';

import { htmlStringToReactElement } from '@common/parser';
import { css } from '@emotion/react';

import { palette } from '@lib/styles/palette';
import pixelToRem from '@lib/styles/pixelToRem';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

interface RecommendKeywordProp {
  heading: string;
  popularKeywords: {
    exhibitionName: string;
    maxWordLength: number;
    reloadDateTime: string;
    wordList: {
      count: number;
      popularWord: string;
      popularWordRegDate: string;
      resetRegDate: boolean;
    }[];
  };
  recommendKeywords: string[];
}

interface ItemProp {
  heading: string;
  description?: string;
  isShown: boolean;
  keywords:
    | RecommendKeywordProp['popularKeywords']['wordList']
    | RecommendKeywordProp['recommendKeywords'];
}

function RecommendKeyword({
  heading,
  popularKeywords,
  recommendKeywords,
}: RecommendKeywordProp): ReactElement {
  const [isPopularKewords, setIsPopularKeywords] = useState(true);

  const setActiveButtonClassName = (isActive: boolean) => {
    return `carouselButton ${isActive ? 'active' : ''}`;
  };

  const sliderRef = useRef<Slider>(null);
  const settings = {
    draggable: false,
    arrows: false,
    dots: false,
    slidesToShow: 2,
    infinite: false,
    swipe: false,
    responsive: [
      {
        breakpoint: 599,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
  };

  const slickGoTo = (index: number) => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    slider.slickGoTo(index);
  };

  return (
    <section css={containerStyle} tabIndex={0}>
      <h2 css={headingStyle}>
        <span className="title">{htmlStringToReactElement(heading)}</span>
      </h2>
      <div css={buttonStyle}>
        <button
          type="button"
          aria-label="live"
          className={setActiveButtonClassName(isPopularKewords)}
          onClick={() => {
            slickGoTo(0);
            setIsPopularKeywords(true);
          }}
        >
          실시간
        </button>
        <button
          type="button"
          aria-label="popular"
          className={setActiveButtonClassName(!isPopularKewords)}
          onClick={() => {
            slickGoTo(1);
            setIsPopularKeywords(false);
          }}
        >
          인기
        </button>
      </div>
      <Slider css={contentStyle} ref={sliderRef} {...settings}>
        <Item
          heading="실시간 시세조회"
          description={popularKeywords.reloadDateTime}
          isShown={isPopularKewords}
          keywords={popularKeywords.wordList}
        />
        <Item
          heading="인기 시세"
          isShown={!isPopularKewords}
          keywords={recommendKeywords}
        />
      </Slider>
    </section>
  );
}

const Item = ({ heading, description, isShown, keywords }: ItemProp) => {
  const fromOneToFive = keywords.slice(0, 5);
  const fromSixToTen = keywords.slice(5, keywords.length);
  const dateStandard = `${dayjs(description).format('YYYY.MM.DD HH:MM')} 기준`;

  const settings = {
    dots: true,
    slidesToShow: 2,
    infinite: false,
    swipeToSlide: true,
    touchThreshold: 1000, // 터치 민감도
    responsive: [
      {
        breakpoint: 599,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
  };

  return (
    <li css={itemStyle(isShown)}>
      <h3>{heading}</h3>
      <p>{description && dateStandard}</p>
      <div className="carousel-container">
        <Slider {...settings}>
          <ul className="keyword_list">
            {fromOneToFive.map((value, id) => {
              const keyword =
                typeof value !== 'string' ? value.popularWord : value;
              return (
                <li key={String(value)} className="keyword_item">
                  <Link href={`/search-price?searchWord=${keyword}`}>
                    <a>
                      <span className="keyword_order">{id + 1}</span>
                      <span className="keyword">{keyword}</span>
                    </a>
                  </Link>
                </li>
              );
            })}
          </ul>
          <ul className="keyword_list">
            {fromSixToTen.map((value, id) => {
              const keyword =
                typeof value !== 'string' ? value.popularWord : value;

              return (
                <li key={String(value)} className="keyword_item">
                  <Link href={`/search-price?searchWord=${keyword}`}>
                    <a>
                      <span className="keyword_order">{id + 6}</span>
                      <span className="keyword">{keyword}</span>
                    </a>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Slider>
      </div>
    </li>
  );
};

const containerStyle = css`
  width: 100%;
  height: auto;
  position: relative;

  /** PC */
  @media (min-width: 1024px) {
    margin-top: ${pixelToRem(92)};
  }
`;

const headingStyle = css`
  color: ${palette.black10};
  display: flex;
  justify-content: space-between;
  font-size: ${pixelToRem(20)};
  line-height: ${pixelToRem(26)};
  letter-spacing: ${pixelToRem(-0.5)};

  .titleIcon {
    vertical-align: middle;
    margin-bottom: 3px;
  }

  @media (max-width: 599px) {
    display: none;
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    display: none;
  }

  @media (min-width: 1024px) {
    padding: ${pixelToRem(0, 40)};
  }
`;

const buttonStyle = css`
  .carouselButton {
    padding: 8px 12px 10px;
    background-color: ${palette.white};
    font-size: 16px;
    line-height: 19px;
    color: ${palette.gray5};

    &:before {
      content: '';
      width: 0;
      transition: width 0.3s;
    }
  }

  .carouselButton.active {
    position: relative;
    color: ${palette.black10};

    &:before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: black;
      display: inline-block;
      transition: width 0.3s;
    }
  }

  /** mobile */
  @media (max-width: 599px) {
    padding: ${pixelToRem(0, 20)};
    border-bottom: 1px solid ${palette.gray0};
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    padding: ${pixelToRem(0, 24)};
    border-bottom: 1px solid ${palette.gray0};
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const contentStyle = css`
  margin: ${pixelToRem(30, 0, 30, 0)};
  border-top: 1px solid ${palette.gray6};
  padding-top: ${pixelToRem(40)};
  min-height: ${pixelToRem(300)};

  /** mobile */
  @media (max-width: 599px) {
    border: 0;
    padding: ${pixelToRem(0)};
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    border: 0;
    padding: ${pixelToRem(0, 0, 0, 24)};
  }

  /** PC */
  @media (min-width: 1024px) {
    margin-left: ${pixelToRem(40)};
    margin-right: ${pixelToRem(40)};
    display: flex;

    .slick-list {
      flex: 1 1 100%;
    }

    .slick-track:not(.carousel-container .slick-track) {
      display: flex;
      max-width: 100%;
    }

    .slick-slide:not(.carousel-container .slick-slide) {
      > div {
        padding: 36px 32px;
        border-radius: 8px;
        border: 1px solid ${palette.gray3};
      }
    }

    .slick-slide:first-of-type:not(.carousel-container .slick-slide) {
      padding-right: 5px;
    }

    .slick-slide:last-of-type:not(.carousel-container .slick-slide) {
      padding-left: 5px;
    }
  }
`;

const itemStyle = (isShown: boolean) => css`
  flex: 1 1 50%;

  h3 {
    font-size: ${pixelToRem(20)};
    line-height: 26px;
  }

  p {
    font-size: ${pixelToRem(12)};
    color: ${palette.gray5};
    margin-top: 8px;
    height: 16px;
  }

  .slick-slider {
    width: 100%;
  }

  .slick-dots {
    margin-top: 16px;
  }

  .slick-dots li {
    margin: 2px;
    width: 2px;
  }

  .slick-dots li.slick-active {
    width: 20px;
  }

  .slick-dots li button:before {
    content: '';
    top: 16px;
    width: 4px;
    height: 4px;
    background-color: ${palette.gray2};
    border-radius: 50%;
  }

  .slick-dots li.slick-active button:before {
    width: 20px;
    height: 4px;
    background-color: ${palette.gray5};
    border-radius: 3px;
    transition: width 0.3s;
  }

  .carousel-container {
    margin-top: 16px;
    width: 100%;
    min-height: 200px;
  }

  .keyword_list {
    width: 100%;
  }

  .keyword_item {
    font-size: ${pixelToRem(14)};
    padding: 10px 4px;
  }

  .keyword_order {
    color: ${palette.orange9};
    display: inline-block;
    margin-right: 8px;
    width: 12px;
  }

  .keyword {
    color: ${palette.black10};
  }

  @media (max-width: 599px) {
    display: block;
    padding-left: 20px;
    max-width: 100%;

    p {
      height: auto;
    }

    .carousel-container {
      margin-top: 26px;
      /* width: 200%; */
    }

    .keyword_list {
      /* width: 200%; */
      flex: 1 0 100%;
    }
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    /* display: ${isShown ? 'block' : 'none'}; */
    display: block;
    max-width: 100%;
  }

  @media (min-width: 1024px) {
    display: initial;
    /* padding: 36px 32px; */
    max-width: 50%;

    .ant-carousel {
      width: 100%;
    }

    .slick-slide {
      padding: 0;
      border: none;
    }
  }
`;

export default RecommendKeyword;
